import { partialRefundNet, refundLineValue } from './partial-refund.js';
import { salePriceTtcFromHt } from './currency.js';

export type RefundMode = 'full' | 'partial';
export type LineMode = 'return' | 'exchange';

export interface ReplacementProduct {
  id: string;
  name: string;
  salePriceHT: number;
  taxRate: number;
}

export interface RefundLineChoice {
  included: boolean;
  qty: number;
  mode: LineMode;
  replacement: ReplacementProduct | null;
}

export interface RefundPricedItem {
  productId: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
  taxRate: number;
  productDiscountPercentage?: number;
  productDiscountAmount?: number;
}

export interface RefundSelectionReceipt<T extends RefundPricedItem = RefundPricedItem> {
  items: T[];
  grandTotal: number;
  returnedQuantities?: number[];
}

export interface RefundExchangeDraft {
  originalProductId: string;
  replacementProductId: string;
  replacementName: string;
  quantity: number;
  unitPrice: number;
  taxRate: number;
}

export interface RefundSelection<T extends RefundPricedItem = RefundPricedItem> {
  items: T[];
  exchanges: RefundExchangeDraft[];
  refundAmount: number;
  collectedAmount: number;
  net: number;
  settled: boolean;
  canContinue: boolean;
  returnValue: number;
  exchangeOutValue: number;
  replacementValue: number;
}

export function lineRemaining(receipt: RefundSelectionReceipt, index: number): number {
  const item = receipt.items[index];
  if (!item) return 0;
  const taken = receipt.returnedQuantities?.[index] ?? 0;
  return Math.max(0, item.quantity - taken);
}

export function canOfferFullRefund(receipt: RefundSelectionReceipt): boolean {
  return receipt.items.every((_, index) => lineRemaining(receipt, index) === receipt.items[index].quantity);
}

export function initialLineChoices(receipt: RefundSelectionReceipt): RefundLineChoice[] {
  return receipt.items.map((_, index) => {
    const max = lineRemaining(receipt, index);
    return {
      included: max > 0,
      qty: max,
      mode: 'return' as const,
      replacement: null,
    };
  });
}

function scaledItem<T extends RefundPricedItem>(item: T, qty: number): T {
  const subtotal =
    item.quantity > 0 ? (item.subtotal / item.quantity) * qty : item.unitPrice * qty;
  return { ...item, quantity: qty, subtotal };
}

export function buildRefundSelection<T extends RefundPricedItem>(
  receipt: RefundSelectionReceipt<T>,
  mode: RefundMode,
  lines: RefundLineChoice[],
): RefundSelection<T> {
  if (mode === 'full') {
    const pairs = receipt.items
      .map((item, index) => ({
        item: scaledItem(item, lineRemaining(receipt, index)),
        source: item,
      }))
      .filter((pair) => pair.item.quantity > 0);
    const items = pairs.map((pair) => pair.item);
    const untouched = canOfferFullRefund(receipt);
    const refundAmount = untouched
      ? receipt.grandTotal
      : pairs.reduce(
          (sum, pair) => sum + refundLineValue(pair.source, pair.item.quantity),
          0,
        );
    return {
      items,
      exchanges: [],
      refundAmount,
      collectedAmount: 0,
      net: refundAmount,
      settled: true,
      canContinue: items.length > 0,
      returnValue: refundAmount,
      exchangeOutValue: 0,
      replacementValue: 0,
    };
  }

  let returnValue = 0;
  let exchangeOutValue = 0;
  let replacementValue = 0;
  const items: T[] = [];
  const exchanges: RefundExchangeDraft[] = [];
  let missingReplacement = false;
  let selected = 0;

  lines.forEach((line, index) => {
    const source = receipt.items[index];
    const max = lineRemaining(receipt, index);
    if (!source || !line.included || line.qty < 1 || max < 1) return;
    const qty = Math.min(line.qty, max);
    selected += 1;
    items.push(scaledItem(source, qty));
    const value = refundLineValue(source, qty);
    if (line.mode === 'exchange') {
      if (!line.replacement) {
        missingReplacement = true;
        return;
      }
      exchangeOutValue += value;
      replacementValue += salePriceTtcFromHt(line.replacement.salePriceHT, line.replacement.taxRate) * qty;
      exchanges.push({
        originalProductId: source.productId,
        replacementProductId: line.replacement.id,
        replacementName: line.replacement.name,
        quantity: qty,
        unitPrice: line.replacement.salePriceHT,
        taxRate: line.replacement.taxRate,
      });
      return;
    }
    returnValue += value;
  });

  const net = partialRefundNet({ returnValue, exchangeOutValue, replacementValue });
  const left = receipt.items.map((_item, index) => {
    const takenNow = lines[index]?.included ? Math.min(lines[index].qty, lineRemaining(receipt, index)) : 0;
    return lineRemaining(receipt, index) - Math.max(0, takenNow);
  });

  return {
    items,
    exchanges,
    refundAmount: net.refundAmount,
    collectedAmount: net.collectedAmount,
    net: net.net,
    settled: left.every((qty) => qty <= 0) && selected > 0,
    canContinue: selected > 0 && !missingReplacement,
    returnValue,
    exchangeOutValue,
    replacementValue,
  };
}
